/* Copyright (c) 2020-present CloudIO Inc */

import { attachmentProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { baseFieldProps } from '../TextField/BaseField';
import { defaultValue } from './AttachmentField';

const AttachmentFieldManager: ComponentManagerFn<'AttachmentField'> = (
  platform: Platform,
  type: 'AttachmentField',
): ComponentManager<'AttachmentField'> => {
  return {
    ...baseComponentManager<'AttachmentField'>(platform, 'AttachmentField'),
    type: 'AttachmentField',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnChange'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      viewAttribute: {
        ...baseFieldProps.viewAttribute,
        getAttributeDataTypes: () => ['Attachment'],
      } as ViewAttributeChooserProp,
      label: {
        label: 'Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      postAPI: {
        label: 'Post to API',
        type: 'boolean',
        helpText: 'Check this to post the file to an API',
      },
      apiURL: {
        label: 'API URL',
        type: 'string',
      },
      apiHeaders: {
        label: 'JSON Data',
        type: 'json',
      },
      helperText: {
        label: 'Help Text',
        type: 'string',
      },
      ...attachmentProps,
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'accept': {
          return !itemData.images;
        }
        case 'colorExpression':
        case 'bgColorExpression':
          return false;
        case 'imageHeight':
        case 'showImage':
        case 'maxImageWidth': {
          return itemData.images;
        }
        case 'apiHeaders':
        case 'apiURL': {
          return itemData.postAPI;
        }
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    name: 'Attachment Field',
    devStyles: { display: 'inline-flex' },
    category: 'Forms',
    icon: { iconName: 'paperclip' },
    description: `Use this to allow user to upload files.
Use \`Trigger On Change\` child action to perform further actions after uploading the file.
\`\`\`javascript
// Access the file information as below using expression builder
\`\${$._.file.fileName} \${$._.file.fileSize} \${$._.file.contentType} \${$._.file.fileVersion} \${$._.file.fileUid}\`
\`\`\`

`,
  };
};

export default AttachmentFieldManager;
